<template>
    <div class="info-content">
        <!-- Group 1: Id -->
        <div class="info-row">
            <span class="row-title">Id</span>
            <span class="row-value">{{ activeClient.id || "-" }}</span>
        </div>

        
        <!-- Group 2: Name, Chain name, Shop name -->
        <div class="info-row">
            <span class="row-title">{{ "Name" | t }}</span>
            <span class="row-value">{{ activeClient.name || "-" }}</span>
        </div>
        <div class="info-row">
            <span class="row-title">{{ "Chain name" | t }}</span>
            <span class="row-value">{{ activeClient.chain_name || "-" }}</span>
        </div>
        <div class="info-row">
            <span class="row-title">{{ "Shop name" | t }}</span>
            <span class="row-value">{{ activeClient.shop_name || "-" }}</span>
        </div>

        
        <!-- Group 3: Business type, Shop type -->
        <div class="info-row">
            <span class="row-title">{{ "Business type" | t }}</span>
            <span class="row-value">{{ activeClient.business_type || "-" }}</span>
        </div>
        <div class="info-row">
            <span class="row-title">{{ "Shop type" | t }}</span>
            <span class="row-value">{{ activeClient.shop_type || "-" }}</span>
        </div>

        
        <!-- Group 4: Comment, Address fields -->
        <div class="info-row">
            <span class="row-title">{{ "Comment" | t }}</span>
            <span class="row-value">{{ activeClient.comment || "-" }}</span>
        </div>
        <div class="info-row">
            <span class="row-title">{{ "Shop address" | t }}</span>
            <span class="row-value">{{ activeClient.shop_address || "-" }}</span>
        </div>
        <div class="info-row">
            <span class="row-title">{{ "Shop place" | t }}</span>
            <span class="row-value">{{ activeClient.shop_place || "-" }}</span>
        </div>
        <div class="info-row">
            <span class="row-title">{{ "Shop parish" | t }}</span>
            <span class="row-value">{{ activeClient.shop_parish || "-" }}</span>
        </div>
        <div class="info-row">
            <span class="row-title">{{ "Shop county" | t }}</span>
            <span class="row-value">{{ activeClient.shop_county || "-" }}</span>
        </div>
        <div class="info-row">
            <span class="row-title">{{ "Shop index" | t }}</span>
            <span class="row-value">{{ activeClient.shop_index || "-" }}</span>
        </div>
        <div class="info-row">
            <span class="row-title">{{ "Shop country" | t }}</span>
            <span class="row-value">{{ activeClient.shop_country || "-" }}</span>
        </div>
        <div class="info-row">
            <span class="row-title">{{ "Shop country code" | t }}</span>
            <span class="row-value">
                <country-flag
                    v-if="activeClient.shop_country_code"
                    :country="activeClient.shop_country_code"
                    size="small"
                    class="mr-1"
                />
                {{ activeClient.shop_country_code || "-" }}
            </span>
        </div>

        
        <!-- Group 5: Latitude, Longitude -->
        <div class="info-row">
            <span class="row-title">{{ "Latitude" | t }}</span>
            <span class="row-value">{{ activeClient.latitude || "-" }}</span>
        </div>
        <div class="info-row">
            <span class="row-title">{{ "Longitude" | t }}</span>
            <span class="row-value">{{ activeClient.longitude || "-" }}</span>
        </div>

        
        <!-- Group 6: Client country, Price list, Contractual relationship -->
        <div class="info-row">
            <span class="row-title">{{ "Client country" | t }}</span>
            <span class="row-value">
                <country-flag
                    v-if="activeClient.country_code"
                    :country="activeClient.country_code"
                    size="small"
                    class="mr-1"
                />
                {{ activeClient.country_estonian || activeClient.country_english || "-" }}
            </span>
        </div>
        <div class="info-row">
            <span class="row-title">{{ "Price list" | t }}</span>
            <span class="row-value">{{ activeClient.price_list || "-" }}</span>
        </div>
        <div class="info-row">
            <span class="row-title">{{ "Contractual relationship" | t }}</span>
            <span class="row-value">{{ activeClient.contractual_relationship || "-" }}</span>
        </div>


        <!-- Export info (export clients only) -->
        <template v-if="isExportClient">
            <div class="info-row">
                <span class="row-title">{{ "Region" | t }}</span>
                <span class="row-value">{{ regionLabel }}</span>
            </div>
            <div class="info-row">
                <span class="row-title">{{ "Met place" | t }}</span>
                <span class="row-value">{{ activeClient.met_place_name || "-" }}</span>
            </div>
            <div class="info-row">
                <span class="row-title">{{ "Met date" | t }}</span>
                <span class="row-value">{{ activeClient.met_date || "-" }}</span>
            </div>
            <div class="info-row">
                <span class="row-title">{{ "Total profit" | t }}</span>
                <span class="row-value">{{ activeClient.total_profit || "-" }}</span>
            </div>

            <!-- Contacts -->
            <div class="info-row align-start">
                <span class="row-title">{{ "Contacts" | t }}</span>
                <span class="row-value">
                    <template v-if="activeClient.contacts && activeClient.contacts.length > 0">
                        <div v-for="contact in activeClient.contacts" :key="contact.id" class="contact-item">
                            <strong>{{ contact.name || "-" }}</strong>
                            <span v-if="contact.position"> — {{ contact.position }}</span>
                            <span v-if="contact.phone"> · {{ contact.phone }}</span>
                            <span v-if="contact.email"> · {{ contact.email }}</span>
                            <span v-if="contact.comment" class="grey--text"> ({{ contact.comment }})</span>
                        </div>
                    </template>
                    <span v-else>-</span>
                </span>
            </div>
        </template>

        <!-- Group 7: Tags -->
        <div class="info-row">
            <span class="row-title">{{ "Tags" | t }}</span>
            <span class="row-value">
                <template v-if="activeClient.tags && activeClient.tags.length > 0">
                    <v-chip
                        v-for="tag in activeClient.tags"
                        :key="tag.id"
                        x-small
                        class="mr-1"
                        @click="goToTag(tag.id)"
                    >
                        {{ tag.name }}
                    </v-chip>
                </template>
                <span v-else>-</span>
            </span>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";
import CountryFlag from "vue-country-flag";

export default {
    name: "client-info-tab",
    components: {
        CountryFlag
    },
    computed: {
        ...mapState("ClientStore", ["activeClient"]),
        isExportClient() {
            return this.activeClient.business_type === "export";
        },
        regionLabel() {
            const labels = {
                none: "-",
                europe: "Europe",
                northern_usa: "Northern USA",
                southern_usa: "Southern USA",
                asia: "Asia",
                oceania: "Oceania",
                africa: "Africa",
                middle_east: "Middle East"
            };
            return labels[this.activeClient.region] || this.activeClient.region || "-";
        }
    },
    methods: {
        goToTag(tagId) {
            this.$router.push({ name: "tag-view", params: { tagId: tagId } });
        }
    }
};
</script>

<style scoped>
.info-content {
    padding: 12px 20px;
}

.info-row {
    display: flex;
    align-items: center;
    min-height: 24px;
    line-height: 1.3;
}

.row-title {
    color: #888;
    width: 140px;
    flex-shrink: 0;
    font-size: 13px;
}

.row-value {
    font-size: 13px;
    color: #333;
}

.info-row.align-start {
    align-items: flex-start;
}

.contact-item {
    font-size: 13px;
    line-height: 1.4;
}

</style>
